import { matchRoutes, type RouteObject } from 'react-router-dom';
import { getPublicRuntimeConfig } from '../../libs/init/getPublicRuntimeConfig';
import { isInternalHref } from './trailingSlash';

const prefetchedRouteIds = new Set<string>();
const inFlightPrefetches = new Map<string, Promise<void>>();

/**
 * Strips query, hash, basename, and trailing slashes from an internal href so
 * it can be passed to `matchRoutes`.
 */
export const hrefToRouteMatchPathname = (href: string): string | null => {
  if (!isInternalHref(href)) {
    return null;
  }

  const beforeHash = href.split('#')[0] ?? href;
  const pathname = (beforeHash.split('?')[0] ?? beforeHash).trim();
  if (pathname === '') {
    return null;
  }

  let normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;

  const basename = getPublicRuntimeConfig().paths.base;
  if (basename !== '' && normalized.startsWith(basename)) {
    normalized = normalized.slice(basename.length) || '/';
  }

  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
};

const prefetchRouteLazy = (route: RouteObject): void => {
  const lazy = route.lazy;
  // RR7 `LazyRouteDefinition` is a loader function *or* a pre-resolved
  // `LazyRouteObject`; only the function form is callable and worth prefetching.
  if (!lazy || !route.id || typeof lazy !== 'function') {
    return;
  }

  if (prefetchedRouteIds.has(route.id)) {
    return;
  }

  const existing = inFlightPrefetches.get(route.id);
  if (existing) {
    return;
  }

  const prefetch = lazy()
    .then(() => {
      prefetchedRouteIds.add(route.id!);
    })
    .catch(() => {
      // Ignore prefetch failures; navigation will retry the lazy import.
    })
    .finally(() => {
      inFlightPrefetches.delete(route.id!);
    });

  inFlightPrefetches.set(route.id, prefetch);
};

/**
 * Warms the lazy route modules for every route in a match chain. Calling the
 * same `lazy()` function RR7 uses keeps webpack's dynamic-import cache hot
 * without mutating the route objects.
 */
export const prefetchMatchedRoutes = (
  matches: ReturnType<typeof matchRoutes> | null,
): void => {
  for (const { route } of matches ?? []) {
    prefetchRouteLazy(route);
  }
};

/**
 * Prefetches code-split route modules for an internal app href. Mirrors Next.js
 * `<Link prefetch>` chunk warming in library-mode React Router: only lazy route
 * modules are prefetched (no loader data). No-op in development and for
 * external URLs.
 */
export const prefetchRoute = (href: string): void => {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  const pathname = hrefToRouteMatchPathname(href);
  if (!pathname) {
    return;
  }

  void import('../../routing/routes').then(({ routes }) => {
    prefetchMatchedRoutes(matchRoutes(routes, pathname));
  });
};
